import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";
import { Switch } from "./ui/switch";
import { Checkbox } from "./ui/checkbox";
import { ScrollArea } from "./ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import { Separator } from "./ui/separator";
import {
  Filter,
  SlidersHorizontal,
  X,
  ChevronDown,
  Home,
  Bath,
  BedDouble,
  CarFront,
  Calendar,
  Plus,
  Building2,
  Globe2
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { Badge } from "./ui/badge";
import { cn, formatPrice } from "@/lib/utils";

export interface FilterState {
  type: 'all' | 'sale' | 'rent' | 'temporal';
  propertyType: 'all' | 'casa' | 'apartamento' | 'departamento' | 'terreno' | 'local' | 'oficina' | 'casa_comercial' | 'industrial' | 'parcelacion';
  country: 'all' | 'venezuela' | 'colombia' | 'ecuador' | 'panama' | 'costa-rica' | 'chile';
  city?: string;
  minPrice?: number;
  maxPrice?: number;
  beds?: number;
  baths?: number;
  superficie?: { min?: number; max?: number };
  estacionamientos?: number;
  antiguedad?: { value?: number; min?: number; max?: number };
  caracteristicas: string[];
  publicadoHoy: boolean;
  tiendaOficial: boolean;
  visitaOnline: boolean;
  priceRange?: { min?: number; max?: number };
}

const PRICE_RANGES = [
  { label: "Hasta $ 100.000.000", min: 0, max: 100000000 },
  { label: "$ 100.000.000 a $ 250.000.000", min: 100000000, max: 250000000 },
  { label: "Más de $ 250.000.000", min: 250000000, max: null }
];

const DORMITORIOS = [
  { label: "Monoambiente", value: 0 },
  { label: "1 dormitorio", value: 1 },
  { label: "2 dormitorios", value: 2 },
  { label: "3 dormitorios", value: 3 },
  { label: "4 dormitorios o más", value: 4 }
];

const BANOS = [
  { label: "1 baño", value: 1 },
  { label: "2 baños", value: 2 },
  { label: "3 baños", value: 3 },
  { label: "4 baños", value: 4 },
  { label: "5 baños o más", value: 5 }
];

const SUPERFICIE = [
  { label: "50 m² o menos", max: 50 },
  { label: "50 a 70 m²", min: 50, max: 70 },
  { label: "70 a 150 m²", min: 70, max: 150 },
  { label: "150 m² o más", min: 150 }
];

const ESTACIONAMIENTOS = [
  { label: "No tiene", value: 0 },
  { label: "1 estacionamiento", value: 1 },
  { label: "2 estacionamientos", value: 2 },
  { label: "3 estacionamientos", value: 3 },
  { label: "4 estacionamientos o más", value: 4 }
];

const ANTIGUEDAD = [
  { label: "A estrenar", value: 0 },
  { label: "1 a 7 años", min: 1, max: 7 },
  { label: "7 a 15 años", min: 7, max: 15 },
  { label: "15 a 50 años", min: 15, max: 50 },
  { label: "50 años o más", min: 50 }
];

const CARACTERISTICAS = [
  { label: "Amoblado", value: "amoblado" },
  { label: "Aire acondicionado", value: "aire" },
  { label: "Jardín", value: "jardin" },
  { label: "Parrilla", value: "parrilla" },
  { label: "Estacionamiento de visitas", value: "estacionamientoVisitas" },
  { label: "Gimnasio", value: "gimnasio" },
  { label: "Baño de visitas", value: "banoVisitas" },
  { label: "Salón de usos múltiples", value: "sum" },
  { label: "Piscina", value: "piscina" }
];

const MAX_PRICE = 1000000; // 1 millón como máximo
const MIN_PRICE = 800; // Precio mínimo basado en los datos

interface FiltersProps {
  filters: FilterState;
  onFilterChange: (name: keyof FilterState, value: FilterState[keyof FilterState]) => void;
}

export function Filters({ filters, onFilterChange }: FiltersProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<FilterState>(filters);
  const [priceRange, setPriceRange] = useState<[number, number]>([
    filters.minPrice || MIN_PRICE,
    filters.maxPrice || MAX_PRICE
  ]);

  const handleFilterChange = (name: keyof FilterState, value: FilterState[keyof FilterState]) => {
    const newFilters = {
      ...localFilters,
      [name]: value
    };
    setLocalFilters(newFilters);
    
    // Aplicar el filtro inmediatamente
    onFilterChange(name, value);
    
    // Si es un cambio en el rango de precios, actualizamos el estado local pero no aplicamos
    // ya que esto se maneja con el slider y queremos evitar múltiples actualizaciones
    if (name === 'minPrice' || name === 'maxPrice') {
      // No hacemos nada adicional, se aplicará cuando el usuario suelte el slider
    }
  };

  const handleApply = () => {
    onFilterChange("minPrice", priceRange[0]);
    onFilterChange("maxPrice", priceRange[1] === MAX_PRICE ? undefined : priceRange[1]);
    setIsOpen(false);
  };

  const handleClear = () => {
    const clearedFilters: FilterState = {
      type: filters.type, // Mantener el tipo de operación actual
      propertyType: filters.propertyType, // Mantener el tipo de propiedad actual
      country: filters.country, // Mantener el país actual
      city: filters.city, // Mantener la ciudad actual
      minPrice: undefined,
      maxPrice: undefined,
      beds: undefined,
      baths: undefined,
      superficie: undefined,
      estacionamientos: undefined,
      antiguedad: undefined,
      caracteristicas: [],
      publicadoHoy: false,
      tiendaOficial: false,
      visitaOnline: false,
      priceRange: undefined
    };
    
    setLocalFilters(clearedFilters);
    setPriceRange([MIN_PRICE, MAX_PRICE]);
    
    // Aplicar solo los filtros avanzados que se han limpiado
    onFilterChange("minPrice", undefined);
    onFilterChange("maxPrice", undefined);
    onFilterChange("beds", undefined);
    onFilterChange("baths", undefined);
    onFilterChange("superficie", undefined);
    onFilterChange("estacionamientos", undefined);
    onFilterChange("antiguedad", undefined);
    onFilterChange("caracteristicas", []);
    onFilterChange("publicadoHoy", false);
    onFilterChange("tiendaOficial", false);
    onFilterChange("visitaOnline", false);
    onFilterChange("priceRange", undefined);
    
    // Cerrar el panel de filtros después de limpiar
    setIsOpen(false);
  };

  const handlePriceRangeChange = (value: number[]) => {
    setPriceRange([value[0], value[1]]);
    
    // Aplicar los cambios de precio inmediatamente
    handleFilterChange("minPrice", value[0]);
    handleFilterChange("maxPrice", value[1] === MAX_PRICE ? undefined : value[1]);
  };

  const clearPriceRange = () => {
    setPriceRange([MIN_PRICE, MAX_PRICE]);
    handleFilterChange("minPrice", undefined);
    handleFilterChange("maxPrice", undefined);
    handleFilterChange("priceRange", undefined);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (priceRange[0] > MIN_PRICE || priceRange[1] < MAX_PRICE) count++;
    if (typeof localFilters.beds === 'number' && localFilters.beds > 0) count++;
    if (typeof localFilters.baths === 'number' && localFilters.baths > 0) count++;
    if (localFilters.superficie?.min || localFilters.superficie?.max) count++;
    if (typeof localFilters.estacionamientos === 'number' && localFilters.estacionamientos > 0) count++;
    if (localFilters.antiguedad?.value !== undefined || localFilters.antiguedad?.min || localFilters.antiguedad?.max) count++;
    if (localFilters.caracteristicas && localFilters.caracteristicas.length > 0) count++;
    if (localFilters.publicadoHoy) count++;
    if (localFilters.tiendaOficial) count++;
    if (localFilters.visitaOnline) count++;
    return count;
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" className="gap-2 relative w-full h-12 uppercase font-black tracking-widest text-xs border-2 border-ink hover:bg-ink hover:text-parchment transition-all rounded-none">
          <SlidersHorizontal className="h-4 w-4" />
          Filtros
          {getActiveFiltersCount() > 0 && (
            <span className="ml-2 h-5 w-5 bg-terracotta text-parchment flex items-center justify-center text-[10px]">
              {getActiveFiltersCount()}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:w-[540px] p-0 bg-parchment border-l-4 border-ink">
        <SheetHeader className="p-8 border-b-2 border-ink">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-3xl font-display font-black uppercase text-ink">Búsqueda Avanzada</SheetTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleClear} 
              className="text-xs font-bold uppercase tracking-widest text-ink hover:text-terracotta transition-colors"
            >
              Resetear
            </Button>
          </div>
        </SheetHeader>
        
        <ScrollArea className="h-[calc(100vh-180px)] px-8">
          <div className="space-y-12 py-12">
            {/* Precio */}
            <div className="space-y-6">
              <h3 className="text-xs font-black uppercase tracking-[0.3em] text-terracotta border-b border-ink/10 pb-2">Rango de Precio</h3>
              <div className="px-2">
                <Slider
                  min={MIN_PRICE}
                  max={MAX_PRICE}
                  step={1000}
                  value={priceRange}
                  onValueChange={handlePriceRangeChange}
                  className="my-4"
                />
              </div>
              <div className="flex justify-between items-center gap-4">
                <Input
                  type="number"
                  value={priceRange[0]}
                  onChange={(e) => handlePriceRangeChange([Number(e.target.value), priceRange[1]])}
                  className="h-10 text-xs rounded-none"
                />
                <span className="text-[10px] font-bold uppercase tracking-widest">A</span>
                <Input
                  type="number"
                  value={priceRange[1]}
                  onChange={(e) => handlePriceRangeChange([priceRange[0], Number(e.target.value)])}
                  className="h-10 text-xs rounded-none"
                />
              </div>
            </div>

            {/* Dormitorios */}
            <div className="space-y-6">
              <h3 className="text-xs font-black uppercase tracking-[0.3em] text-terracotta border-b border-ink/10 pb-2">Espacios</h3>
              <div className="space-y-6">
                <div className="space-y-3">
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-ink/40">Dormitorios</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {DORMITORIOS.map((option) => (
                      <Button
                        key={option.value}
                        variant={localFilters.beds === option.value ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleFilterChange("beds", option.value)}
                        className="text-[10px] h-8 rounded-none"
                      >
                        {option.label}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-ink/40">Baños</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {BANOS.map((option) => (
                      <Button
                        key={option.value}
                        variant={localFilters.baths === option.value ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleFilterChange("baths", option.value)}
                        className="text-[10px] h-8 rounded-none"
                      >
                        {option.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Características */}
            <div className="space-y-6">
              <h3 className="text-xs font-black uppercase tracking-[0.3em] text-terracotta border-b border-ink/10 pb-2">Comodidades</h3>
              <div className="grid grid-cols-1 gap-4">
                {CARACTERISTICAS.map((option) => (
                  <div key={option.value} className="flex items-center justify-between border-b border-ink/5 pb-2">
                    <span className="text-xs font-bold uppercase tracking-widest text-ink">{option.label}</span>
                    <Checkbox
                      checked={localFilters.caracteristicas.includes(option.value)}
                      onCheckedChange={(checked) => {
                        const newFeatures = checked
                          ? [...localFilters.caracteristicas, option.value]
                          : localFilters.caracteristicas.filter(f => f !== option.value);
                        handleFilterChange("caracteristicas", newFeatures);
                      }}
                      className="rounded-none border-2 border-ink data-[state=checked]:bg-terracotta"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ScrollArea>
        <div className="p-8 border-t-2 border-ink bg-white">
          <Button 
            className="w-full h-14 text-sm font-black uppercase tracking-[0.2em] bg-ink text-parchment hover:bg-terracotta transition-colors rounded-none"
            onClick={() => setIsOpen(false)}
          >
            Ver Resultados
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}